import React from 'react'

/**
 * Performance monitoring utilities for React components and API calls.
 *
 * Web vitals and unhandled errors are captured automatically by PostHog
 * (capture_performance + capture_exceptions). These helpers add lightweight
 * console-level timing for slow renders / API calls during development.
 */

/**
 * Measure React component render performance
 * @example
 * const MyComponent = () => {
 *   usePerformanceMonitor('MyComponent', { slowThreshold: 100 })
 *   return <div>Content</div>
 * }
 */
export function usePerformanceMonitor(
  componentName: string,
  options: {
    slowThreshold?: number
    enableLogging?: boolean
  } = {},
) {
  const { slowThreshold = 200, enableLogging = true } = options

  React.useEffect(() => {
    const startTime = performance.now()

    return () => {
      const duration = performance.now() - startTime

      if (duration > slowThreshold && enableLogging) {
        console.warn(
          `⚠️  Slow render: ${componentName} took ${duration.toFixed(2)}ms`,
          {
            component: componentName,
            duration: `${duration.toFixed(2)}ms`,
            threshold: `${slowThreshold}ms`,
          },
        )
      }
    }
  }, [componentName, slowThreshold, enableLogging])
}

/**
 * Measure page load performance
 * Call this once per page/route
 */
export function measurePageLoad(pageName: string) {
  if (typeof window === 'undefined') return

  console.debug(`Page loaded: ${pageName}`)
}

/**
 * Measure API call performance
 * Wraps ts-rest or fetch calls with timing
 */
export async function measureApiCall<T>(
  apiName: string,
  apiCall: () => Promise<T>,
  options: {
    slowThreshold?: number
  } = {},
): Promise<T> {
  const { slowThreshold = 1000 } = options
  const startTime = performance.now()

  const result = await apiCall()
  const duration = performance.now() - startTime

  if (duration > slowThreshold) {
    console.warn(
      `⚠️  Slow API call: ${apiName} took ${duration.toFixed(2)}ms`,
      {
        api: apiName,
        duration: `${duration.toFixed(2)}ms`,
        threshold: `${slowThreshold}ms`,
      },
    )
  }

  return result
}

/**
 * Measure dashboard component load time
 * Specifically for the Advanced Dashboard
 */
export function useDashboardLoadMonitor() {
  const [loadStart] = React.useState(() => performance.now())
  const [isComplete, setIsComplete] = React.useState(false)

  React.useEffect(() => {
    if (!isComplete) {
      const duration = performance.now() - loadStart

      if (duration > 5000) {
        console.warn(
          `⚠️  Dashboard loading slowly: ${duration.toFixed(2)}ms elapsed`,
          { duration: `${duration.toFixed(2)}ms` },
        )
      }
    }
  }, [isComplete, loadStart])

  const markComplete = React.useCallback(() => {
    const duration = performance.now() - loadStart
    setIsComplete(true)

    console.log(`✅ Dashboard loaded in ${duration.toFixed(2)}ms`)

    return duration
  }, [loadStart])

  return { markComplete, loadStart }
}

/**
 * Create a performance mark for measuring specific operations
 */
export async function measureOperation<T>(
  operationName: string,
  operation: () => T | Promise<T>,
): Promise<T> {
  const startTime = performance.now()

  const result = await operation()
  const duration = performance.now() - startTime

  if (duration > 100) {
    console.debug(`${operationName} took ${duration.toFixed(2)}ms`)
  }

  return result
}
